import React from "react";
import { Box, Divider } from "@mui/material";

import { useBaseTabsContext } from "../BaseTabs";
import { useBaseTabPanelContext } from "./BaseTabPanel";

import HoverablePaper from "components/Shared/HoverablePaper";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import EmptyResult from "components/Shared/Lottie/EmptyResult";

import { getMuiIcon } from "utils";

import { BASE_CONSTANTS } from "constants/baseConstants";
import BaseCheckbox from "./BaseCheckbox";

export const PanelList = ({
  loading = false,
  listItems = [],
  listItemComponent: ListItemComponent = null,
  getSelectedItemValue = () => {},
}) => {
  const { activeTabDetail } = useBaseTabsContext();

  const { state, dispatch } = useBaseTabPanelContext();

  // methods
  const handleOnItemSelect = React.useCallback(
    (item) => {
      const _existing = !!state.selectedItems?.find(
        (el) => el === getSelectedItemValue(item)
      );

      dispatch({
        selectedItems: !_existing
          ? [...state.selectedItems, getSelectedItemValue(item)]
          : state?.selectedItems?.filter(
              (el) => el !== getSelectedItemValue(item)
            ),
      });
    },
    [dispatch, getSelectedItemValue, state.selectedItems]
  );

  const renderIcon = React.useCallback(
    (item) => {
      if (activeTabDetail?.multiSelect)
        return (
          <BaseCheckbox
            checked={
              !!state?.selectedItems?.includes(getSelectedItemValue(item))
            }
            size="small"
            onClick={() => handleOnItemSelect(item)}
          />
        );

      if (!activeTabDetail?.icon) return null;

      return getMuiIcon(activeTabDetail?.icon, {
        color: "primary",
        fontSize: "small",
        style: { marginTop: 2 },
      });
    },
    [
      activeTabDetail?.icon,
      activeTabDetail?.multiSelect,
      getSelectedItemValue,
      handleOnItemSelect,
      state?.selectedItems,
    ]
  );

  // initializing available items for selection actions
  React.useEffect(() => {
    if (!listItems?.length) return;

    if (listItems?.length === state.availableItems?.length) return;

    dispatch({
      availableItems: listItems?.map((el) => getSelectedItemValue(el)),
    });
  }, [dispatch, getSelectedItemValue, listItems, state.availableItems?.length]);

  if (loading) return <RowsSkeleton />;

  return listItems?.length ? (
    listItems?.map((item, index) => (
      <React.Fragment key={index}>
        <HoverablePaper>
          <Box
            display="flex"
            alignItems="start"
            padding={BASE_CONSTANTS.PADDING_MD}
            gap={BASE_CONSTANTS.PADDING_SM}
          >
            {renderIcon(item)}

            <Box flexGrow={1}>
              <ListItemComponent data={item} />
            </Box>
          </Box>
        </HoverablePaper>

        {index !== listItems.length - 1 && (
          <Box paddingX={BASE_CONSTANTS.PADDING_SM} paddingY={0.5}>
            <Divider />
          </Box>
        )}
      </React.Fragment>
    ))
  ) : (
    <EmptyResult height={150} label={`No ${activeTabDetail?.label}!`} />
  );
};
